import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  ArrowLeft, Users, DollarSign, Building2, Calendar,
  CheckCircle, Loader2, AlertTriangle, FileText
} from 'lucide-react';
import { employeeApi, payrollApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { cn, formatCurrency } from '../../lib/utils';

interface BankSegment {
  id: string;
  bank_name: string;
  bank_code: string;
  employee_count: number;
}

const PayrollRunNew: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [payrollMonth, setPayrollMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedBank, setSelectedBank] = useState<string>('all');
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState(1); // 1: Select, 2: Review, 3: Process
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch employees
  const { data: employees = [], isLoading } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeeApi.getAll({}),
  });

  // Mock bank segments (in real app, calculate from employee bank details)
  const bankSegments: BankSegment[] = [
    { id: 'chase', bank_name: 'Chase Bank', bank_code: 'CHB001', employee_count: 45 },
    { id: 'bofa', bank_name: 'Bank of America', bank_code: 'BOA002', employee_count: 32 },
    { id: 'wells', bank_name: 'Wells Fargo', bank_code: 'WF003', employee_count: 18 },
    { id: 'citi', bank_name: 'Citibank', bank_code: 'CITI004', employee_count: 12 },
  ];

  // Filter employees by selected bank
  const filteredEmployees = selectedBank === 'all'
    ? employees
    : (employees as any[]).filter((emp: any) => {
      // In real app, filter by emp.bank_name matching selectedBank
      // For now, distribute randomly for demo
      const hash = emp.id.charCodeAt(0) % bankSegments.length;
      return bankSegments[hash].id === selectedBank;
    });

  const toggleEmployeeSelection = (employeeId: string) => {
    setSelectedEmployees(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedEmployees.length === filteredEmployees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees((filteredEmployees as any[]).map((emp: any) => emp.id));
    }
  };

  const getEmployeeName = (emp: any) => {
    return `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
  };

  const getEmployeeInitials = (emp: any) => {
    const first = emp.first_name?.charAt(0) || '';
    const last = emp.last_name?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  const calculateTotalPayroll = () => {
    return selectedEmployees.reduce((sum, employeeId) => {
      const emp = (employees as any[]).find((e: any) => e.id === employeeId);
      const baseSalary = emp?.base_salary ? parseFloat(emp.base_salary) : 0;
      return sum + baseSalary;
    }, 0);
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      if (selectedEmployees.length === 0) {
        toast({
          title: 'No employees selected',
          description: 'Please select at least one employee',
          variant: 'destructive'
        });
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      setCurrentStep(3);
      setIsProcessing(true);

      // Calculate pay period dates from selected month
      const [year, month] = payrollMonth.split('-');
      const payPeriodStart = `${year}-${month}-01`;
      const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
      const payPeriodEnd = `${year}-${month}-${lastDay.toString().padStart(2, '0')}`;
      const payDate = payPeriodEnd; // Pay on last day of month

      // Create payroll run data
      const payrollRunData = {
        pay_period_start: payPeriodStart,
        pay_period_end: payPeriodEnd,
        pay_date: payDate,
        status: 'PAID',
        total_amount: calculateTotalPayroll(),
        employee_count: selectedEmployees.length,
        employee_ids: selectedEmployees,
        bank_segment: selectedBank === 'all' ? null : selectedBank,
        notes: `Payroll run for ${formatMonth(payrollMonth)}`
      };

      try {
        // Create the payroll run
        await payrollApi.runs.create(payrollRunData);

        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['payroll-runs'] });
        queryClient.invalidateQueries({ queryKey: ['salary-slips'] });

        toast({
          title: 'Payroll run successful!',
          description: `Processed payroll for ${selectedEmployees.length} employees`
        });

        // Navigate to salary slips page
        setTimeout(() => {
          navigate('/payroll/slips');
        }, 1000);
      } catch (error: any) {
        setIsProcessing(false);
        toast({
          title: 'Payroll run failed',
          description: error.message || 'Failed to process payroll',
          variant: 'destructive'
        });
        setCurrentStep(2); // Go back to review step
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/payroll/runs');
    }
  };


  const formatMonth = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 font-bold text-slate-400">Loading employees...</span>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-300">
      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-2 text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
        <span className="hover:text-slate-500 transition-colors cursor-pointer" onClick={() => navigate('/payroll')}>Payroll</span>
        <span className="text-slate-300 dark:text-slate-700">/</span>
        <span className="hover:text-slate-500 transition-colors cursor-pointer" onClick={() => navigate('/payroll/runs')}>Payroll Runs</span>
        <span className="text-slate-300 dark:text-slate-700">/</span>
        <span className="text-[#143A62] dark:text-white font-black">New Run</span>
      </nav>

      {/* Header */}
      <div className="flex items-center gap-3">
        <Button 
          variant="outline" 
          size="icon"
          onClick={handleBack}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-2xl md:text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">
          Select employees and process payroll for {formatMonth(payrollMonth)}
        </h2>
      </div>

      {/* Progress Steps */}
      <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-center justify-between w-full">
            {[
              { step: 1, label: 'Select Employees', icon: Users },
              { step: 2, label: 'Review & Confirm', icon: FileText },
              { step: 3, label: 'Process Payroll', icon: CheckCircle },
            ].map((item, index) => {
              const Icon = item.icon;
              const isCompleted = currentStep > item.step;
              const isCurrent = currentStep === item.step;

              return (
                <React.Fragment key={item.step}>
                  <div className="flex flex-col items-center text-center space-y-2 shrink-0">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 shrink-0",
                      isCompleted 
                        ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20' 
                        : isCurrent 
                          ? 'bg-[#143A62] text-white shadow-md shadow-[#143A62]/20 dark:bg-blue-600' 
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-400'
                    )}>
                      {isCompleted ? (
                        <CheckCircle className="h-5 w-5" />
                      ) : (
                        <Icon className="h-5 w-5" />
                      )}
                    </div>
                    <p className={cn(
                      "text-xs font-bold transition-all duration-300 whitespace-nowrap",
                      isCurrent ? 'text-[#143A62] dark:text-white font-black' : 'text-slate-400 dark:text-slate-500'
                    )}>
                      {item.label}
                    </p>
                  </div>
                  {index < 2 && (
                    <div className={cn(
                      "h-0.5 flex-1 mx-4 transition-all duration-300 min-w-[20px]",
                      isCompleted ? 'bg-emerald-500' : 'bg-slate-100 dark:bg-slate-800'
                    )}></div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Step 1: Select Employees */}
      {currentStep === 1 && (
        <>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden rounded-2xl hover:translate-y-[-2px] transition-all duration-300">
              <CardContent className="p-6">
                <Label className="text-xs font-bold text-slate-400 tracking-wider">Payroll Month</Label>
                <Input
                  type="month"
                  value={payrollMonth}
                  onChange={(e) => setPayrollMonth(e.target.value)}
                  className="mt-2.5 rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                />
              </CardContent>
            </Card>

            <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden rounded-2xl hover:translate-y-[-2px] transition-all duration-300">
              <CardContent className="p-6">
                <Label className="text-xs font-bold text-slate-400 tracking-wider">Bank Segment</Label>
                <Select value={selectedBank} onValueChange={setSelectedBank}>
                  <SelectTrigger className="mt-2.5 rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11 shadow-sm bg-white dark:bg-slate-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-slate-100 dark:border-slate-800">
                    <SelectItem value="all" className="text-sm font-bold">All Banks</SelectItem>
                    {bankSegments.map(bank => (
                      <SelectItem key={bank.id} value={bank.id} className="text-sm font-bold">
                        {bank.bank_name} ({bank.employee_count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden rounded-2xl hover:translate-y-[-2px] transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="text-sm font-bold text-slate-400 tracking-wide">Selected</p>
                    <p className="text-3xl font-bold text-slate-800 dark:text-white leading-none mt-1">{selectedEmployees.length}</p>
                  </div>
                  <div className="p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-[#143A62] dark:text-blue-450 shadow-sm shrink-0">
                    <Users className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Bank Segments Info */}
          <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
            <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
              <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Bank Segments Overview</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                {bankSegments.map(bank => (
                  <div
                    key={bank.id}
                    className={cn(
                      "p-5 border rounded-2xl cursor-pointer transition-all duration-300 flex flex-col justify-between hover:shadow-sm",
                      selectedBank === bank.id
                        ? 'border-[#143A62] bg-[#143A62]/5 dark:border-blue-500 dark:bg-blue-950/10'
                        : 'border-slate-100 dark:border-slate-800 hover:border-[#143A62]/30 dark:hover:border-blue-500/30'
                    )}
                    onClick={() => setSelectedBank(bank.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="p-2.5 rounded-xl bg-blue-50 dark:bg-blue-955/20 text-[#143A62] dark:text-blue-400">
                        <Building2 className="h-5 w-5" />
                      </div>
                      {selectedBank === bank.id && (
                        <CheckCircle className="h-5 w-5 text-emerald-500" />
                      )}
                    </div>
                    <div className="mt-4">
                      <p className="font-bold text-xs text-slate-750 dark:text-slate-350">{bank.bank_name}</p>
                      <p className="text-[9px] font-bold text-slate-400 mt-0.5 tracking-wider uppercase">{bank.bank_code}</p>
                    </div>
                    <p className="text-xs font-bold text-slate-500 mt-3.5">
                      <span className="font-black text-[#143A62] dark:text-blue-450">{bank.employee_count}</span> employees
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Employees Selection */}
          <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
            <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50 flex flex-row items-center justify-between space-y-0 gap-4 flex-wrap">
              <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Select Employees</CardTitle>
              <div className="flex items-center space-x-4">
                <span className="text-xs font-bold text-slate-400 tracking-wider">
                  {selectedEmployees.length} of {filteredEmployees.length} selected
                </span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={toggleSelectAll}
                >
                  {selectedEmployees.length === filteredEmployees.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="space-y-3 p-6 max-h-96 overflow-y-auto custom-scrollbar">
                {(filteredEmployees as any[]).map((employee: any) => (
                  <div
                    key={employee.id}
                    className={cn(
                      "flex items-center space-x-4 p-4 border rounded-2xl cursor-pointer transition-all duration-300 hover:shadow-sm",
                      selectedEmployees.includes(employee.id)
                        ? 'border-[#143A62] bg-[#143A62]/5 dark:border-blue-500 dark:bg-blue-955/10 shadow-sm'
                        : 'border-slate-100 dark:border-slate-800 hover:border-[#143A62]/20 dark:hover:border-blue-500/20'
                    )}
                    onClick={() => toggleEmployeeSelection(employee.id)}
                  >
                    <Checkbox
                      checked={selectedEmployees.includes(employee.id)}
                      onCheckedChange={() => toggleEmployeeSelection(employee.id)}
                      className="rounded-md border-slate-300 dark:border-slate-700 data-[state=checked]:bg-[#143A62] dark:data-[state=checked]:bg-blue-600"
                    />
                    <Avatar className="h-10 w-10 ring-2 ring-slate-100 dark:ring-slate-800 shadow-sm shrink-0">
                      <AvatarImage src={employee.avatar_url} />
                      <AvatarFallback className="bg-gradient-to-br from-[#143A62]/10 to-blue-500/10 text-[#143A62] font-black text-xs">
                        {getEmployeeInitials(employee)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-xs text-slate-750 dark:text-slate-350 truncate">{getEmployeeName(employee)}</p>
                      <p className="text-[10px] font-bold text-slate-400 mt-0.5 uppercase tracking-wider truncate">{employee.job_title || 'N/A'}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-black text-xs text-[#143A62] dark:text-blue-450">{formatCurrency(employee.base_salary ? parseFloat(employee.base_salary) : 0)}</p>
                      <p className="text-[9px] font-bold text-slate-400 mt-0.5 tracking-wider uppercase">Base Salary</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Step 2: Review & Confirm */}
      {currentStep === 2 && (
        <>
          <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
            <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
              <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Payroll Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 p-6">
                <div className="border border-slate-100 dark:border-slate-800 rounded-2xl p-5 bg-slate-50/50 dark:bg-slate-800/10 shadow-sm">
                  <p className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Payroll Month</p>
                  <p className="text-sm font-bold mt-2 text-slate-750 dark:text-slate-300">{formatMonth(payrollMonth)}</p>
                </div>
                <div className="border border-slate-100 dark:border-slate-800 rounded-2xl p-5 bg-slate-50/50 dark:bg-slate-800/10 shadow-sm">
                  <p className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Bank Segment</p>
                  <p className="text-sm font-bold mt-2 text-slate-750 dark:text-slate-300">
                    {selectedBank === 'all' ? 'All Banks' : bankSegments.find(b => b.id === selectedBank)?.bank_name}
                  </p>
                </div>
                <div className="border border-slate-100 dark:border-slate-800 rounded-2xl p-5 bg-slate-50/50 dark:bg-slate-800/10 shadow-sm">
                  <p className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Employees</p>
                  <p className="text-sm font-bold mt-2 text-slate-750 dark:text-slate-300">{selectedEmployees.length}</p>
                </div>
                <div className="border border-emerald-100 dark:border-emerald-950/20 rounded-2xl p-5 bg-emerald-50 dark:bg-emerald-955/10 shadow-sm">
                  <p className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Total Amount</p>
                  <p className="text-xl font-black text-emerald-600 dark:text-emerald-450 mt-2">{formatCurrency(calculateTotalPayroll())}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
            <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
              <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Selected Employees ({selectedEmployees.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="space-y-3 p-6 max-h-96 overflow-y-auto custom-scrollbar">
                {(employees as any[]).filter((emp: any) => selectedEmployees.includes(emp.id)).map((employee: any) => (
                  <div key={employee.id} className="flex items-center space-x-4 p-4 border border-slate-100 dark:border-slate-800 rounded-2xl bg-white dark:bg-slate-900">
                    <Avatar className="h-10 w-10 ring-2 ring-slate-100 dark:ring-slate-800 shadow-sm shrink-0">
                      <AvatarImage src={employee.avatar_url} />
                      <AvatarFallback className="bg-gradient-to-br from-[#143A62]/10 to-blue-500/10 text-[#143A62] font-black text-xs">
                        {getEmployeeInitials(employee)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-xs text-slate-750 dark:text-slate-350 truncate">{getEmployeeName(employee)}</p>
                      <p className="text-[9px] font-bold text-slate-400 mt-0.5 tracking-wider uppercase truncate">{employee.employee_number || employee.id.slice(0, 8)}</p>
                    </div>
                    <div className="text-right shrink-0 flex flex-col items-end gap-1.5">
                      <p className="font-black text-xs text-[#143A62] dark:text-blue-450">{formatCurrency(employee.base_salary ? parseFloat(employee.base_salary) : 0)}</p>
                      <Badge className="px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none bg-emerald-50 text-emerald-700 border-emerald-200/60 dark:bg-emerald-955/20 dark:text-emerald-450" variant="outline">Ready</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border border-orange-100 dark:border-orange-950/20 bg-orange-50 dark:bg-orange-955/10 rounded-2xl overflow-hidden shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-start space-x-3.5">
                <div className="p-2.5 rounded-xl bg-orange-100/60 dark:bg-orange-950/30 text-orange-600 dark:text-orange-400 shrink-0">
                  <AlertTriangle className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-bold text-xs text-orange-900 dark:text-orange-200 uppercase tracking-wider mt-1">
                    Confirm Before Processing
                  </h3>
                  <p className="text-xs font-semibold text-orange-700/90 dark:text-orange-300 mt-2 leading-relaxed">
                    Please review all details carefully. Once processed, this payroll run cannot be reversed.
                    Salary will be deposited to employee bank accounts based on their saved bank details.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Step 3: Processing */}
      {currentStep === 3 && (
        <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
          <CardContent className="py-20 p-6">
            <div className="text-center max-w-md mx-auto">
              <Loader2 className="h-16 w-16 animate-spin text-blue-600 mx-auto shrink-0" />
              <h2 className="text-2xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight mt-6">Processing Payroll...</h2>
              <p className="text-xs font-semibold text-slate-400 mt-2">
                Please wait while we process payroll for {selectedEmployees.length} employees
              </p>
              <div className="mt-8 p-6 rounded-2xl border border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 space-y-3 text-left">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                  <p>✓ Validating employee bank details</p>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                  <p>✓ Calculating salaries and deductions</p>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                  <p>✓ Generating salary slips</p>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-blue-600 dark:text-blue-400 animate-pulse">
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-650 shrink-0" />
                  <p>● Processing payments...</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      {currentStep < 3 && (
        <div className="flex justify-end items-center gap-3">
          <Button 
            variant="outline" 
            onClick={handleBack}
          >
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </Button>
          <Button 
            onClick={handleNext} 
          >
            <span>{currentStep === 1 ? 'Continue to Review' : 'Process Payroll'}</span>
            {currentStep === 2 && <DollarSign className="h-4 w-4 shrink-0" />}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PayrollRunNew;
